/**
 * User-facing country directory for phone selectors.
 *
 * Each entry: { iso, name, dial }.
 *   iso  → ISO 3166-1 alpha-2 (this is what we still STORE in `phone_country`)
 *   name → friendly Spanish name shown to the user
 *   dial → international calling code (with leading "+")
 *
 * The list is intentionally restricted to the same set the IBAN/phone
 * validators already support (`SUPPORTED_COUNTRIES`) so we keep a single
 * source of truth: a country either validates and is selectable, or it
 * is not exposed at all.
 *
 * `prioritized` is rendered first inside dropdowns so the most common
 * cases for the business (Spain + nearby) don't require scrolling.
 */
export const COUNTRY_DIRECTORY = [
  { iso: "ES", name: "España",         dial: "+34" },
  { iso: "PT", name: "Portugal",       dial: "+351" },
  { iso: "FR", name: "Francia",        dial: "+33" },
  { iso: "AD", name: "Andorra",        dial: "+376" },
  { iso: "IT", name: "Italia",         dial: "+39" },
  { iso: "DE", name: "Alemania",       dial: "+49" },
  { iso: "GB", name: "Reino Unido",    dial: "+44" },
  { iso: "IE", name: "Irlanda",        dial: "+353" },
  { iso: "NL", name: "Países Bajos",   dial: "+31" },
  { iso: "BE", name: "Bélgica",        dial: "+32" },
  { iso: "LU", name: "Luxemburgo",     dial: "+352" },
  { iso: "AT", name: "Austria",        dial: "+43" },
  { iso: "CH", name: "Suiza",          dial: "+41" },
  { iso: "PL", name: "Polonia",        dial: "+48" },
  { iso: "CZ", name: "Chequia",        dial: "+420" },
  { iso: "SK", name: "Eslovaquia",     dial: "+421" },
  { iso: "HU", name: "Hungría",        dial: "+36" },
  { iso: "RO", name: "Rumanía",        dial: "+40" },
  { iso: "BG", name: "Bulgaria",       dial: "+359" },
  { iso: "GR", name: "Grecia",         dial: "+30" },
  { iso: "DK", name: "Dinamarca",      dial: "+45" },
  { iso: "NO", name: "Noruega",        dial: "+47" },
  { iso: "SE", name: "Suecia",         dial: "+46" },
  { iso: "FI", name: "Finlandia",      dial: "+358" },
  { iso: "IS", name: "Islandia",       dial: "+354" },
  { iso: "EE", name: "Estonia",        dial: "+372" },
  { iso: "LV", name: "Letonia",        dial: "+371" },
  { iso: "LT", name: "Lituania",       dial: "+370" },
  { iso: "HR", name: "Croacia",        dial: "+385" },
  { iso: "SI", name: "Eslovenia",      dial: "+386" },
  { iso: "MT", name: "Malta",          dial: "+356" },
  { iso: "CY", name: "Chipre",         dial: "+357" },
  { iso: "TR", name: "Turquía",        dial: "+90" },
  { iso: "US", name: "Estados Unidos", dial: "+1" },
  { iso: "BR", name: "Brasil",         dial: "+55" },
  { iso: "AE", name: "Emiratos Árabes Unidos", dial: "+971" },
  { iso: "SA", name: "Arabia Saudí",   dial: "+966" },
  { iso: "IL", name: "Israel",         dial: "+972" },
];

const ISO_TO_ENTRY = Object.fromEntries(COUNTRY_DIRECTORY.map(c => [c.iso, c]));

export const getCountryEntry = (iso) => ISO_TO_ENTRY[(iso || "ES").toUpperCase()] || ISO_TO_ENTRY.ES;

/**
 * Match the longest dial code prefix found in the raw phone input.
 * Accepts both "+34..." and "0034..." styles. Returns the ISO code or
 * null when no prefix matches a known country.
 */
export const detectCountryFromPhone = (raw) => {
  if (!raw) return null;
  let s = String(raw).replace(/[\s().-]/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  if (!s.startsWith("+")) return null;
  // Sort dial codes by length (longest first) so "+1242" wins over "+1".
  const sorted = [...COUNTRY_DIRECTORY].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (s.startsWith(c.dial)) return c.iso;
  }
  return null;
};

/**
 * Formatted label for dropdown rows: "España (+34)".
 */
export const formatCountryLabel = (entry) => `${entry.name} (${entry.dial})`;

/**
 * Customer-data formatter for read-only phone displays. When a `phone_country`
 * is known we prefix the visible value with the dial code; otherwise we
 * return the raw phone untouched. Used in CustomerDetail / opportunity
 * cards to keep phone presentation consistent.
 */
export const formatPhoneWithCountry = (phone, isoCode) => {
  if (!phone) return "";
  const entry = ISO_TO_ENTRY[(isoCode || "").toUpperCase()];
  if (!entry) return phone;
  // Avoid double prefix when the stored phone already begins with the dial.
  const trimmed = String(phone).trim();
  if (trimmed.startsWith(entry.dial) || trimmed.startsWith(entry.dial.replace("+", "00"))) return trimmed;
  return `${entry.dial} ${trimmed}`;
};
